<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import type { AdminRecord } from '@/types/record'
import { pageById } from '@/data/registry'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import { adapter } from '@/services/prototype-adapter'
import { request } from '@/services/request'
import { toPlain } from '@/utils/clone'
import { formatDateTime } from '@/utils/format'
import AppIcon from '@/components/AppIcon.vue'
import ProSelect from '@/components/pro/ProSelect.vue'

const definition = pageById('decoration')
if (!definition) throw new Error('装修中心页面定义缺失')

const auth = useAuthStore()
const ui = useUiStore()
const pages = ref<AdminRecord[]>([])
const selectedKey = ref('')
const config = ref<AdminRecord | null>(null)
const components = ref<AdminRecord[]>([])
const selectedId = ref('')
const mode = ref<'preview' | 'edit'>('edit')
const loading = ref(true)
const loadingConfig = ref(false)
const error = ref('')
const saving = ref<'draft' | 'published' | ''>('')
const saveError = ref('')
const dirty = ref(false)
const dragIndex = ref(-1)
const overIndex = ref(-1)
const filter = ref('')
const history = ref<string[]>([])
const future = ref<string[]>([])

const canManage = computed(() => auth.has('decoration.manage'))
const canSave = computed(() => auth.has('decoration.save'))
const selectedPage = computed(() => pages.value.find(page => page.page_key === selectedKey.value))
const selectedComponent = computed(() => components.value.find(item => String(item.id) === selectedId.value) ?? null)
const filteredPages = computed(() => pages.value.filter(page => !filter.value || `${page.title}${page.full_path}`.toLowerCase().includes(filter.value.toLowerCase())))
const visibleComponents = computed(() => components.value.filter(item => item.visible !== false))
const linkOptions = computed(() => adapter.options('links'))
const assetOptions = computed(() => adapter.options('assets'))

const LIBRARY: Array<{ type: string; label: string; description: string; scopes: string[] }> = [
  { type: 'banner', label: '顶部横幅', description: '品牌主视觉与运营文案', scopes: ['页面内容', '运营位'] },
  { type: 'quick-entry', label: '快捷入口', description: '常用业务功能入口', scopes: ['快捷入口', '运营位'] },
  { type: 'metric-card', label: '健康指标卡', description: '展示用户最近健康指标', scopes: ['页面内容'] },
  { type: 'family-card', label: '家庭守护卡', description: '家庭成员与守护关系', scopes: ['页面内容'] },
  { type: 'tip-card', label: '健康贴士卡', description: '运营贴士与科普内容', scopes: ['页面内容', '运营位'] },
  { type: 'notice', label: '公告条', description: '平台公告与风险提示', scopes: ['运营位'] },
  { type: 'medication-card', label: '用药计划卡', description: '今日用药与提醒状态', scopes: ['页面内容'] },
  { type: 'service-entry', label: '服务入口', description: '客服、报告与会员服务', scopes: ['快捷入口', '运营位'] },
]

const library = computed(() => {
  const scopes = Array.isArray(selectedPage.value?.edit_scope) ? selectedPage.value.edit_scope.map(String) : ['页面内容', '快捷入口', '运营位']
  return LIBRARY.filter(item => item.scopes.some(scope => scopes.includes(scope)))
})

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

function remember(): void {
  history.value.push(JSON.stringify(components.value))
  if (history.value.length > 40) history.value.shift()
  future.value = []
}

function undo(): void {
  const previous = history.value.pop()
  if (!previous) return
  future.value.push(JSON.stringify(components.value))
  components.value = JSON.parse(previous) as AdminRecord[]
  selectedId.value = String(components.value[0]?.id ?? '')
  dirty.value = true
}

function redo(): void {
  const next = future.value.pop()
  if (!next) return
  history.value.push(JSON.stringify(components.value))
  components.value = JSON.parse(next) as AdminRecord[]
  selectedId.value = String(components.value[0]?.id ?? '')
  dirty.value = true
}

function loadPageConfig(): void {
  if (!selectedKey.value) return
  loadingConfig.value = true
  error.value = ''
  try {
    const found = adapter.decorationConfig(selectedKey.value)
    if (!found) {
      config.value = null
      components.value = []
      error.value = '当前页面尚未注册装修配置'
      return
    }
    config.value = found
    components.value = clone((found.components as AdminRecord[] | undefined) ?? [])
    selectedId.value = String(components.value[0]?.id ?? '')
    history.value = []
    future.value = []
    dirty.value = false
    saveError.value = ''
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : '装修配置加载失败'
  } finally {
    loadingConfig.value = false
  }
}

async function selectPage(key: string): Promise<void> {
  if (key === selectedKey.value) return
  if (dirty.value) {
    const allowed = await ui.confirm({
      title: '切换装修页面',
      message: '当前页面有未保存的装修修改，切换页面会放弃这些修改。',
      confirmText: '放弃并切换',
      cancelText: '继续编辑',
      tone: 'danger',
    })
    if (!allowed) return
  }
  selectedKey.value = key
  mode.value = 'edit'
  dirty.value = false
}

function selectComponent(id: string): void {
  selectedId.value = id
}

function addComponent(type: string): void {
  if (!canManage.value) {
    ui.toast('warning', '没有装修权限', '当前角色只能查看装修配置。')
    return
  }
  const item = LIBRARY.find(entry => entry.type === type)
  if (!item) return
  remember()
  const id = `diy-${Date.now()}-${components.value.length + 1}`
  components.value.push({
    id,
    type,
    label: item.label,
    title: item.label,
    subtitle: item.description,
    visible: true,
    sort: components.value.length + 1,
  })
  selectedId.value = id
  dirty.value = true
  void nextTick(() => document.querySelector('.diy-component.is-selected')?.scrollIntoView({ block: 'nearest' }))
}

function removeComponent(): void {
  if (!selectedComponent.value) return
  remember()
  components.value = components.value.filter(item => item.id !== selectedComponent.value?.id)
  selectedId.value = String(components.value[0]?.id ?? '')
  dirty.value = true
}

function move(direction: -1 | 1): void {
  const index = components.value.findIndex(item => String(item.id) === selectedId.value)
  const target = index + direction
  if (index < 0 || target < 0 || target >= components.value.length) return
  remember()
  const [item] = components.value.splice(index, 1)
  if (item) components.value.splice(target, 0, item)
  dirty.value = true
}

function onDragStart(index: number): void {
  dragIndex.value = index
}

function onDragOver(index: number): void {
  overIndex.value = index
}

function onDrop(index: number): void {
  const from = dragIndex.value
  dragIndex.value = -1
  overIndex.value = -1
  if (from < 0 || from === index) return
  remember()
  const [item] = components.value.splice(from, 1)
  if (item) components.value.splice(index, 0, item)
  dirty.value = true
}

function updateComponent(patch: Record<string, unknown>): void {
  const current = selectedComponent.value
  if (!current) return
  remember()
  Object.assign(current, patch)
  dirty.value = true
}

async function save(status: 'draft' | 'published'): Promise<void> {
  if (!canSave.value || saving.value) return
  if (!config.value) return
  saving.value = status
  saveError.value = ''
  try {
    const normalized = components.value.map((item, index) => ({ ...toPlain(item), sort: index + 1 }))
    const result = await request(
      () => adapter.saveDecoration(selectedKey.value, normalized, status, auth.profile?.name ?? '管理员'),
      { write: true, delay: 420, useSimulation: true },
    )
    config.value = { ...config.value, ...result }
    dirty.value = false
    ui.toast('success', status === 'published' ? '装修已保存并同步' : '装修草稿已保存', `页面版本已更新至 v${result.version}，仅当前原型内存生效。`)
  } catch (caught) {
    saveError.value = caught instanceof Error ? caught.message : '保存失败，请重试'
  } finally {
    saving.value = ''
  }
}

function resetDraft(): void {
  if (!config.value) return
  remember()
  components.value = clone((config.value.components as AdminRecord[] | undefined) ?? [])
  dirty.value = false
  saveError.value = ''
}

function reload(): void {
  pages.value = adapter.decorationPages()
  if (!selectedKey.value && pages.value[0]) selectedKey.value = String(pages.value[0].page_key)
  loadPageConfig()
}

watch(selectedKey, () => loadPageConfig())

function beforeUnload(event: BeforeUnloadEvent): void {
  if (!dirty.value) return
  event.preventDefault()
  event.returnValue = ''
}

onBeforeRouteLeave(async () => {
  if (!dirty.value) return true
  return ui.confirm({
    title: '离开装修中心',
    message: '装修配置有未保存的修改，离开将放弃这些内容。',
    confirmText: '放弃并离开',
    cancelText: '继续编辑',
    tone: 'danger',
  })
})

onMounted(() => {
  window.addEventListener('beforeunload', beforeUnload)
  loading.value = true
  try {
    reload()
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : '页面注册表加载失败'
  } finally {
    loading.value = false
  }
})

onBeforeUnmount(() => window.removeEventListener('beforeunload', beforeUnload))
</script>

<template>
  <section class="decoration-page">
    <header class="page-header">
      <div class="page-header__text">
        <div class="page-header__title-row">
          <h1 class="page-title">装修中心</h1>
          <span class="prototype-pill">原型数据</span>
          <span class="tag" :class="dirty ? 'tag--warning' : 'tag--success'">{{ dirty ? '有未保存修改' : '已保存' }}</span>
        </div>
        <p class="page-subtitle">{{ definition.purpose }}</p>
      </div>
      <div class="page-header__actions">
        <button type="button" class="btn" :disabled="!history.length" @click="undo"><AppIcon name="history" :size="14" />撤销</button>
        <button type="button" class="btn" :disabled="!future.length" @click="redo"><AppIcon name="refresh" :size="14" />重做</button>
        <div class="segmented" role="tablist" aria-label="装修模式">
          <button type="button" class="segmented__item" :class="{ 'segmented__item--active': mode === 'preview' }" @click="mode = 'preview'">实时预览</button>
          <button type="button" class="segmented__item" :class="{ 'segmented__item--active': mode === 'edit' }" @click="mode = 'edit'">DIY 装修</button>
        </div>
        <button type="button" class="btn" :disabled="!canSave || Boolean(saving) || !dirty" @click="save('draft')">
          <span v-if="saving === 'draft'" class="button-spinner" />保存草稿
        </button>
        <button type="button" class="btn btn--primary" :disabled="!canSave || Boolean(saving) || !dirty" @click="save('published')">
          <span v-if="saving === 'published'" class="button-spinner" />保存并同步
        </button>
      </div>
    </header>

    <div class="info-banner info-banner--brand" style="margin-bottom: 12px">
      <AppIcon name="info" :size="14" />
      <span>当前页面：<b>{{ selectedPage?.title ?? '未选择' }}</b> · 完整路径 <code>{{ selectedPage?.full_path ?? '—' }}</code> · 装修配置只在当前浏览器内存保存，不写入数据库。</span>
    </div>

    <div v-if="loading" class="app-loading" style="min-height: 360px">
      <div class="app-loading__card"><span class="app-loading__ring" /><span>正在加载用户端页面注册表…</span></div>
    </div>

    <div v-else-if="error && !pages.length" class="empty-state" role="alert">
      <span class="empty-state__art"><AppIcon name="warning" :size="44" /></span>
      <h3 class="empty-state__title">装修页面加载失败</h3>
      <p class="empty-state__text">{{ error }}</p>
      <div class="empty-state__actions"><button type="button" class="btn btn--primary" @click="reload">重试</button></div>
    </div>

    <div v-else class="diy-layout">
      <aside class="diy-pages">
        <div class="diy-pages__head">用户端页面（{{ pages.length }}）</div>
        <div style="padding: 8px 8px 0">
          <el-input v-model="filter" size="small" placeholder="搜索页面或路径" clearable aria-label="搜索装修页面" />
        </div>
        <div class="diy-pages__list">
          <button v-for="page in filteredPages" :key="String(page.page_key)" type="button" class="diy-page" :class="{ 'is-active': selectedKey === page.page_key }" @click="selectPage(String(page.page_key))">
            <AppIcon name="decoration" :size="13" />
            <span style="min-width: 0; flex: 1">
              {{ page.title }}
              <small>{{ page.full_path }}</small>
            </span>
          </button>
          <p v-if="!filteredPages.length" class="diy-inspector__empty">没有匹配的页面</p>
        </div>
      </aside>

      <main>
        <div v-if="loadingConfig" class="app-loading" style="min-height: 380px"><div class="app-loading__card"><span class="app-loading__ring" /><span>正在加载页面配置…</span></div></div>
        <div v-else-if="error" class="empty-state" style="min-height: 340px">
          <h3 class="empty-state__title">{{ error }}</h3>
          <div class="empty-state__actions"><button type="button" class="btn" @click="loadPageConfig">重试</button></div>
        </div>
        <div v-else-if="mode === 'preview'" class="mock-phone">
          <div class="mock-phone__bar"><span>9:41</span><span>康护健康 · 预览</span><span>100%</span></div>
          <div class="mock-phone__screen">
            <div class="mock-phone__banner">
              <b>{{ selectedPage?.title }}</b>
              <small>{{ selectedPage?.full_path }}</small>
            </div>
            <div v-for="component in visibleComponents" :key="String(component.id)" class="diy-component" style="cursor: default; margin-top: 8px">
              <div class="diy-component__head"><AppIcon name="grid" :size="12" /><b>{{ component.title || component.label }}</b></div>
              <div class="diy-component__body">{{ component.subtitle || component.label }}</div>
            </div>
            <p v-if="!visibleComponents.length" class="diy-inspector__empty">该页面尚未配置可见组件</p>
          </div>
        </div>
        <div v-else class="diy-canvas" @click.self="selectedId = ''">
          <div
            v-for="(component, index) in components"
            :key="String(component.id)"
            class="diy-component"
            :class="{ 'is-selected': selectedId === String(component.id), 'is-dragging': dragIndex === index, 'is-over': overIndex === index }"
            draggable="true"
            tabindex="0"
            @click.stop="selectComponent(String(component.id))"
            @keydown.enter="selectComponent(String(component.id))"
            @dragstart="onDragStart(index)"
            @dragover.prevent="onDragOver(index)"
            @drop.prevent="onDrop(index)"
          >
            <div class="diy-component__head">
              <AppIcon name="grid" :size="12" />
              <b>{{ component.title || component.label }}</b>
              <span class="tag tag--neutral tag--plain">{{ component.type }}</span>
              <span v-if="component.visible === false" class="tag tag--warning">已隐藏</span>
              <span class="diy-component__handle"><AppIcon name="more-horizontal" :size="14" /></span>
            </div>
            <div class="diy-component__body">{{ component.subtitle || component.label }}</div>
          </div>
          <div v-if="!components.length" class="diy-inspector__empty">从右侧组件库添加第一个装修组件</div>
        </div>
        <div v-if="saveError" class="info-banner info-banner--danger" style="margin-top: 10px" role="alert">
          <AppIcon name="warning" :size="14" /><span>{{ saveError }}</span>
          <button type="button" class="text-button" @click="save('draft')">重试保存</button>
        </div>
        <div class="page-toolbar" style="margin-top: 10px">
          <span class="muted">配置版本 v{{ config?.version ?? 1 }} · 最近保存 {{ formatDateTime(config?.saved_at) }} · {{ config?.saved_by ?? '—' }}</span>
          <span class="page-toolbar__spacer" />
          <button type="button" class="btn btn--sm" :disabled="!dirty" @click="resetDraft">放弃修改</button>
        </div>
      </main>

      <aside>
        <div class="diy-library">
          <div class="diy-library__head">组件库 · 按当前页面匹配</div>
          <div class="diy-library__list">
            <button v-for="item in library" :key="item.type" type="button" class="diy-library__item" :disabled="!canManage || mode === 'preview'" @click="addComponent(item.type)">
              <AppIcon name="plus" :size="12" />
              <span><b>{{ item.label }}</b><small style="display:block;color:var(--c-faint);font-size:8.5px">{{ item.description }}</small></span>
            </button>
          </div>
        </div>

        <div class="diy-inspector" style="margin-top: 12px">
          <div class="diy-inspector__head">属性设置</div>
          <div v-if="selectedComponent" class="diy-inspector__body">
            <label class="pro-field">
              <span class="pro-field__label">组件标题</span>
              <el-input :model-value="String(selectedComponent.title ?? '')" maxlength="30" :disabled="!canManage" @update:model-value="updateComponent({ title: $event })" />
            </label>
            <label class="pro-field">
              <span class="pro-field__label">辅助说明</span>
              <el-input :model-value="String(selectedComponent.subtitle ?? '')" type="textarea" :rows="2" maxlength="80" :disabled="!canManage" @update:model-value="updateComponent({ subtitle: $event })" />
            </label>
            <label class="pro-field">
              <span class="pro-field__label">跳转链接</span>
              <ProSelect
                :model-value="String(selectedComponent.link_name ?? '')"
                :options="linkOptions"
                placeholder="不跳转"
                clearable
                :filterable="true"
                :disabled="!canManage"
                aria-label="组件跳转链接"
                @update:model-value="updateComponent({ link_name: $event })"
              />
            </label>
            <label class="pro-field">
              <span class="pro-field__label">封面素材</span>
              <ProSelect
                :model-value="String(selectedComponent.cover_name ?? '')"
                :options="assetOptions"
                placeholder="不使用附件库素材"
                clearable
                :disabled="!canManage"
                aria-label="组件封面素材"
                @update:model-value="updateComponent({ cover_name: $event })"
              />
            </label>
            <label class="checkbox-line">
              <el-switch :model-value="selectedComponent.visible !== false" :disabled="!canManage" @update:model-value="updateComponent({ visible: $event })" />
              <span>用户端可见</span>
            </label>
            <div class="diy-inspector__actions">
              <button type="button" class="btn btn--sm" :disabled="!canManage" @click="move(-1)">上移</button>
              <button type="button" class="btn btn--sm" :disabled="!canManage" @click="move(1)">下移</button>
              <button type="button" class="btn btn--sm btn--danger" :disabled="!canManage" @click="removeComponent">删除</button>
            </div>
          </div>
          <div v-else class="diy-inspector__empty">选择画布中的组件后可编辑标题、说明、可见性与顺序。</div>
        </div>
      </aside>
    </div>
  </section>
</template>

<style scoped>
.button-spinner { width: 12px; height: 12px; border: 2px solid rgba(255,255,255,.4); border-top-color: #fff; border-radius: 50%; animation: spin .8s linear infinite; }
.diy-component.is-dragging { opacity: .45; }
.diy-component.is-over { border-color: var(--c-brand); }
</style>
